import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wrench, RefreshCw, Smartphone, Laptop, Headphones, Star, Award, Truck, CreditCard, Lock, Sparkles, Recycle } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1612442058361-178007e5e498?crop=entropy&cs=srgb&fm=jpg&w=1400&q=85";
const REPAIR_IMG = "https://images.unsplash.com/photo-1550041473-d296a3a8a18a?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85";

const categories = [
  { key: "phones", title: "Phones", desc: "Latest flagships & affordable choices", icon: Smartphone, img: "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?w=800&q=80" },
  { key: "laptops", title: "Laptops", desc: "MacBook, Dell, Lenovo and more", icon: Laptop, img: "https://images.unsplash.com/photo-1496181133206-80ce9b88a853?w=800&q=80" },
  { key: "accessories", title: "Accessories", desc: "Chargers, cases, earbuds, cables", icon: Headphones, img: "https://images.unsplash.com/photo-1583863788434-e58a36330cf0?w=800&q=80" },
];

const conditionTabs = [
  { key: "new", title: "Brand new", desc: "Sealed in box · 12-month warranty · UK stock", icon: Sparkles, img: "https://images.unsplash.com/photo-1556656793-08538906a9f8?w=900&q=80" },
  { key: "used", title: "Second-hand", desc: "Refurbished by JAS · fully tested · 12-month warranty", icon: Recycle, img: "https://images.unsplash.com/photo-1565536421961-fbcc7df00ae9?w=900&q=80" },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-slate-50 jas-grid-bg" data-testid="home-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="overline mb-5 text-[#1a2d5e]">Est. JAS Computers &amp; Mobiles · Unlimited Solutions</div>
            <h1 className="font-display font-black text-5xl lg:text-7xl text-[#0b1838] leading-[0.95] tracking-tighter">
              The phone you want.<br/>
              <span className="text-[#1a2d5e]">The price you deserve.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
              New &amp; pre-owned phones and laptops. Certified repairs in under 2 hours. Generous trade-in offers on every brand. All backed by our 12-month warranty.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="home-hero-shop-cta" className="inline-flex items-center gap-2 px-7 py-4 bg-[#1a2d5e] text-white rounded-lg font-semibold hover:bg-[#0b1838] transition-colors">
                Shop Now <ArrowRight size={18} />
              </Link>
              <Link to="/trade-in" data-testid="home-hero-trade-cta" className="inline-flex items-center gap-2 px-7 py-4 border border-slate-300 text-slate-900 rounded-lg font-semibold hover:bg-white hover:border-[#1a2d5e] transition-colors">
                Get a Trade-in Quote
              </Link>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-600" data-testid="home-trust-badges">
              <div className="flex items-center gap-2"><ShieldCheck size={18} className="text-[#1a2d5e]" /> 12-Mo Warranty</div>
              <div className="flex items-center gap-2"><Lock size={18} className="text-[#1a2d5e]" /> Secure Payments</div>
              <div className="flex items-center gap-2"><Award size={18} className="text-[#1a2d5e]" /> Certified Technicians</div>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-6 bg-[#aedfff]/40 rounded-3xl rotate-3" />
            <img src={HERO_IMG} alt="Smartphone hero" className="relative rounded-2xl shadow-2xl ring-1 ring-slate-200 w-full object-cover h-[480px]" />
            <div className="absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-xl p-5 shadow-xl max-w-xs">
              <div className="overline text-[#1a2d5e]">This week</div>
              <div className="font-display font-bold text-xl mt-1">Up to 40% off accessories</div>
              <div className="text-xs text-slate-500 mt-1">Limited stock · in-store &amp; online</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="home-categories-grid">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="overline">Browse by category</div>
            <h2 className="font-display font-black text-3xl lg:text-4xl mt-2 text-[#0b1838]">What can we get you today?</h2>
          </div>
          <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#1a2d5e] hover:underline">See all <ArrowRight size={16} /></Link>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {categories.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.key} to={`/shop?cat=${c.key}`} className="group bg-white border border-slate-200 rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-xl hover:border-slate-300 transition-all" data-testid={`category-card-${c.key}`}>
                <div className="aspect-[4/3] bg-slate-50 overflow-hidden">
                  <img src={c.img} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3"><Icon size={20} className="text-[#1a2d5e]" /><span className="overline">{c.title}</span></div>
                  <div className="font-display font-bold text-2xl mt-2 text-[#0b1838]">{c.title}</div>
                  <p className="text-sm text-slate-600 mt-1">{c.desc}</p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1a2d5e] group-hover:gap-2 transition-all">Browse {c.title.toLowerCase()} <ArrowRight size={14} /></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* NEW vs SECOND-HAND */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-4 pb-8" data-testid="home-condition-tabs">
        <div className="overline">Choose your style</div>
        <h2 className="font-display font-black text-3xl lg:text-4xl mt-2 text-[#0b1838]">Buy new — or save big with second-hand.</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-6">
          {conditionTabs.map((c) => {
            const Icon = c.icon;
            return (
              <Link key={c.key} to={`/shop?cond=${c.key}`} data-testid={`home-cond-card-${c.key}`} className="group relative overflow-hidden rounded-2xl border border-slate-200 bg-white h-72">
                <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0b1838]/95 via-[#0b1838]/40 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-8 text-white">
                  <Icon className="text-[#aedfff]" size={28} />
                  <div className="font-display font-black text-3xl mt-2 tracking-tight">{c.title}</div>
                  <div className="text-sm text-slate-200 mt-1">{c.desc}</div>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#aedfff] group-hover:gap-2 transition-all">Browse {c.title.toLowerCase()} <ArrowRight size={14} /></div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="home-services-bento">
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 relative overflow-hidden rounded-2xl border border-slate-200 bg-[#0b1838] text-white p-8 lg:p-12">
            <div className="overline text-[#aedfff] mb-3">Repair service</div>
            <h3 className="font-display font-black text-3xl lg:text-5xl tracking-tighter">Cracked, slow, or dead?<br/>We bring it back in 2 hrs.</h3>
            <p className="text-slate-300 mt-4 max-w-md">From screen replacements to motherboard rework — certified technicians, original parts, 90-day repair warranty.</p>
            <Link to="/repairs" data-testid="home-repairs-cta" className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#aedfff] text-[#0b1838] font-semibold rounded-lg hover:bg-white transition-colors">
              <Wrench size={16} /> Book a Repair
            </Link>
            <img src={REPAIR_IMG} alt="repair" className="hidden md:block absolute right-0 bottom-0 w-1/2 h-full object-cover opacity-30" />
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-8">
            <div className="overline mb-3">Trade-in</div>
            <h3 className="font-display font-black text-2xl tracking-tight text-[#0b1838]">Old phone? Instant value.</h3>
            <p className="text-slate-600 mt-3 text-sm leading-relaxed">Tell us your device &amp; condition. Get a quote in minutes. Use it as credit toward your next upgrade.</p>
            <Link to="/trade-in" data-testid="home-trade-cta" className="inline-flex items-center gap-2 mt-6 px-5 py-3 bg-[#1a2d5e] text-white font-semibold rounded-lg hover:bg-[#0b1838] transition-colors">
              <RefreshCw size={16} /> Get a Quote
            </Link>
          </div>
        </div>
      </section>

      {/* MARKETING STRIP */}
      <section className="bg-slate-50 mt-12 py-16" data-testid="home-marketing-strip">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid md:grid-cols-4 gap-8">
          {[
            { icon: Truck, title: "Free UK delivery", desc: "On orders over £200" },
            { icon: ShieldCheck, title: "12-month warranty", desc: "On every device" },
            { icon: CreditCard, title: "Pay your way", desc: "Cards, cash, or trade-in" },
            { icon: Star, title: "5-star service", desc: "1,200+ happy customers" },
          ].map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-white border border-slate-200"><Icon size={22} className="text-[#1a2d5e]" /></div>
                <div>
                  <div className="font-display font-bold text-[#0b1838]">{it.title}</div>
                  <div className="text-sm text-slate-500 mt-1">{it.desc}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* POS PROMO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20" data-testid="home-pos-promo">
        <div className="rounded-3xl border border-slate-200 bg-gradient-to-br from-[#0b1838] to-[#1a2d5e] text-white p-10 lg:p-16 relative overflow-hidden">
          <div className="overline text-[#aedfff] mb-3">For phone shop owners</div>
          <h3 className="font-display font-black text-3xl lg:text-5xl tracking-tighter max-w-3xl">Run your phone shop on the same software we use every day.</h3>
          <p className="mt-4 text-slate-300 max-w-2xl">JAS POS — inventory, repairs tracking, sales, and staff permissions. Designed by phone-shop owners for phone-shop owners.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/pos-system" data-testid="home-pos-cta" className="inline-flex items-center gap-2 px-6 py-3 bg-[#aedfff] text-[#0b1838] font-semibold rounded-lg hover:bg-white transition-colors">Discover JAS POS <ArrowRight size={16} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
